<template>
  <Modal
    content-class="full-height column no-wrap overflow-hidden"
    :full-height="isMobile"
    :modal-style="modalStyle"
    persistent
    @close="handleCloseModal"
  >
    <template #header>
      <div
        class="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-3"
        data-testid="header"
      >
        <div class="tw:text-lg tw:font-bold">
          {{ $t("modals.saml_integration_modal.title") }}
        </div>
      </div>
    </template>

    <div class="tw:flex tw:h-full tw:flex-1 tw:py-4 tw:pl-8 tw:pr-12">
      <div>
        <img
          class="tw:mr-4 tw:max-w-12"
          src="https://assets.streem.com.au/logos/saml.svg"
        />
      </div>
      <div class="tw:flex tw:flex-col tw:overflow-auto">
        <div class="tw:mb-6 tw:text-lg tw:font-bold">
          {{ $t("modals.saml_integration_modal.title") }}
        </div>
        <p class="tw:mt-4">
          {{ $t("modals.saml_integration_modal.sub_title") }}
        </p>
        <BaseSeparator />
        <p class="tw:mt-4">
          {{ $t("modals.saml_integration_modal.instructions_a") }}
        </p>
        <div class="tw:mt-4 tw:flex">
          <InputText
            class="tw:mr-14 tw:w-56"
            disabled
            :label="$t('modals.saml_integration_modal.acs_url')"
            :model-value="acsUrl"
            new-design
          >
            <template #append>
              <BaseButton
                :aria-label="$t('global.copy_to_clipboard')"
                class="tw:bg-denim-900! tw:hover:border-denim-900!"
                flat
                icon="copy"
                icon-class="tw:text-white tw:bg-denim-900 tw:h-full"
                icon-size="lg"
                new-design
                new-icon
                @click="copyValueToClipboard(acsUrl)"
              />
            </template>
          </InputText>
          <InputText
            class="tw:w-56"
            disabled
            :label="$t('modals.saml_integration_modal.audience')"
            :model-value="audience"
            new-design
          >
            <template #append>
              <BaseButton
                :aria-label="$t('global.copy_to_clipboard')"
                class="tw:bg-denim-900! tw:hover:border-denim-900!"
                flat
                icon="copy"
                icon-class="tw:text-white tw:bg-denim-900 tw:h-full"
                icon-size="lg"
                new-design
                new-icon
                @click="copyValueToClipboard(audience)"
              />
            </template>
          </InputText>
        </div>
        <p class="tw:mt-4">
          {{ $t("modals.saml_integration_modal.copy_instructions") }}
        </p>
        <div class="tw:w-1/2">
          <InputText
            v-model="props.samlIntegration.idpUrl"
            class="tw:mt-4"
            :label="$t('modals.saml_integration_modal.sso_url')"
            new-design
            :placeholder="
              $t('modals.saml_integration_modal.sso_url_placeholder')
            "
          >
            <template #appendLabel>
              <span class="tw:text-red-500"> *</span>
            </template>
          </InputText>
          <InputText
            v-model="props.samlIntegration.idpEntityId"
            class="tw:mt-4"
            :label="$t('modals.saml_integration_modal.identity_id')"
            new-design
            :placeholder="
              $t('modals.saml_integration_modal.identity_id_placeholder')
            "
          >
            <template #appendLabel>
              <span class="tw:text-red-500"> *</span>
            </template>
          </InputText>
          <InputText
            v-model="props.samlIntegration.idpCertificate"
            class="tw:mt-4"
            :label="$t('modals.saml_integration_modal.certificate')"
            new-design
            :placeholder="
              $t('modals.saml_integration_modal.certificate_placeholder')
            "
            type="textarea"
          >
            <template #appendLabel>
              <span class="tw:text-red-500"> *</span>
            </template>
          </InputText>
        </div>
        <DomainVerfication
          v-model="props.samlIntegration"
          :is-loading="props.isLoading"
          :on-save-integration="onSaveIntegration"
          :on-verify-domains="onVerifyDomains"
        />
        <CustomAttributes v-model="props.samlIntegration" />
        <ScimSetup v-model="props.samlIntegration" />
      </div>
    </div>
    <template #buttons>
      <div class="tw:flex tw:flex-1 tw:justify-between">
        <div>
          <BaseButton
            color="danger"
            :disabled="disabledRemoveIntegrationButton()"
            :label="$t('modals.saml_integration_modal.remove_integration')"
            new-design
            @click="handleRemoveIntegrationClick"
          />
        </div>

        <div>
          <BaseButton
            :label="$t('global.cancel')"
            new-design
            @click="handleCloseModal"
          />
          <BaseButton
            color="primary"
            :disabled="props.isLoading || disabledIntegrateButton()"
            :label="$t('modals.saml_integration_modal.integrate')"
            :loading="props.isLoading"
            new-design
            @click="onSaveIntegration"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { type CSSProperties, computed, inject, ref } from "vue";

import { BaseButton, BaseSeparator, InputText } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import CustomAttributes from "shared/components/SamlIntegration/CustomAttributes.vue";
import DomainVerfication from "shared/components/SamlIntegration/DomainVerfication.vue";
import ScimSetup from "shared/components/SamlIntegration/ScimSetup.vue";
import useClipboard from "shared/composables/useClipboard";
import { identityBaseUrl } from "shared/constants";
import type { SamlIntegration } from "shared/resources";

const { copyToClipboard } = useClipboard();
const isMobile = inject<boolean>("isMobile");
const acsUrl = ref(`${identityBaseUrl}/saml/consume`);
const audience = ref(`${identityBaseUrl}/saml/metadata`);

const emit = defineEmits<{
  closeModal: [];
  saveIntegration: [];
  removeIntegration: [];
  verifyDomains: [];
}>();

const props = defineProps<{
  samlIntegration: SamlIntegration;
  isLoading: false;
}>();

const modalStyle = computed<CSSProperties>(() => {
  if (isMobile) {
    return {};
  }

  return { maxWidth: "860px", height: "640px" };
});

function disabledIntegrateButton(): boolean {
  return (
    !props.samlIntegration.idpUrl ||
    !props.samlIntegration.idpEntityId ||
    !props.samlIntegration.idpCertificate
  );
}

function disabledRemoveIntegrationButton(): boolean {
  return !props.samlIntegration?.id;
}

function handleCloseModal() {
  emit("closeModal");
}

function onSaveIntegration() {
  emit("saveIntegration");
}

function handleRemoveIntegrationClick() {
  emit("removeIntegration");
}

function copyValueToClipboard(value: string) {
  copyToClipboard(value);
}

function onVerifyDomains() {
  emit("verifyDomains");
}
</script>
